import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Star, Check, Warning, ArrowLeft, ChartLineUp, Info, ArrowRight, X, ShareNetwork, Copy, WhatsappLogo, Link as LinkIcon } from '@phosphor-icons/react';
import GolfWaitlistModal from '../components/GolfWaitlistModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const assetUrl = (url) => url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null;
const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };
const cs = (code) => CURRENCY_SYMBOLS[code] || code + ' ';

// Convert any hex (#fff or #ffffff) to rgba so we can use brand colours
// for translucent rings/shadows alongside Tailwind's solid utilities.
const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(255,255,255,${alpha})`;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// WCAG contrast: returns dark or white text based on background luminance
const getContrastColor = (hex) => {
  if (!hex) return '#FFFFFF';
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L > 0.4 ? '#0A0A0A' : '#FFFFFF';
};

// World Cup Trophy image
const WORLD_CUP_TROPHY = "https://images.unsplash.com/photo-1637203727700-9d86c74904d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHx3b3JsZCUyMGN1cCUyMHRyb3BoeSUyMGdvbGR8ZW58MHx8fHwxNzc1NzI2NDYzfDA&ixlib=rb-4.1.0&q=85&w=400";

const DEMO_PRIZES = [
  { position: 1, title: '1st Place', description: '\u20ac500 Cash Prize', sponsor_name: "Murphy's Bar & Grill", sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/518c71a17cd11ddccf73e05840d6b8afed5d0d12a5ff3019d8633feb7da15fc2.png' },
  { position: 2, title: '2nd Place', description: '\u20ac200 Voucher', sponsor_name: 'Acme Builders Ltd', sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/8b1eb4f8c38d5052b459106dd1d04386bae7c74b8a766dcda32e70abdd41d42d.png' },
  { position: 3, title: '3rd Place', description: '\u20ac100 Gift Card', sponsor_name: 'Village Pharmacy', sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/9db7f47051371bf0c967f92694bee019c8cc4da30e9e93dfdbda4643b888f6c4.png' },
];

function DemoWatermark() {
  return (
    <div className="fixed inset-0 z-[55] pointer-events-none overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0" style={{
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 80px,
          rgba(29, 53, 87, 0.024) 80px,
          rgba(29, 53, 87, 0.024) 82px
        )`,
      }}>
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute whitespace-nowrap select-none"
            style={{
              top: `${(i * 180) - 100}px`,
              left: '-200px',
              right: '-200px',
              transform: 'rotate(-35deg)',
              transformOrigin: 'center center',
              fontSize: '72px',
              fontWeight: 900,
              fontFamily: 'Barlow Condensed, sans-serif',
              letterSpacing: '0.15em',
              color: 'rgba(29, 53, 87, 0.036)',
              textTransform: 'uppercase',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO
          </div>
        ))}
      </div>
    </div>
  );
}

// Points system
const POINTS_SYSTEM = [
  { stage: "Group Stage", points: 1, description: "Team qualifies from group" },
  { stage: "Round of 32", points: 2, description: "Win in Round of 32" },
  { stage: "Round of 16", points: 3, description: "Win in Round of 16" },
  { stage: "Quarter Final", points: 5, description: "Win Quarter Final" },
  { stage: "Semi Final", points: 8, description: "Win Semi Final" },
  { stage: "Final", points: 12, description: "Reach the Final" },
  { stage: "Winner", points: 20, description: "Win the World Cup!" },
];

// Kick-off: 11 June 2026, 08:00 Irish Time (IST = UTC+1)
// Opening match kicks off 20:00 BST/IST on 11 June 2026 (= 19:00 UTC).
const KICKOFF = new Date('2026-06-11T19:00:00Z');

// Email-capture modal extracted to /app/frontend/src/components/GolfWaitlistModal.js
// so the Open Golf demo's Activate banner can reuse the same form.
// (See import at top of file.)



function Countdown() {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const calc = () => {
      const diff = KICKOFF - Date.now();
      if (diff <= 0) return setTimeLeft(null);
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ days, hours, minutes, seconds });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, []);

  if (!timeLeft) return null;

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hrs', value: timeLeft.hours },
    { label: 'Mins', value: timeLeft.minutes },
    { label: 'Secs', value: timeLeft.seconds },
  ];

  return (
    <div className="mt-6" data-testid="countdown">
      <p className="text-xs uppercase tracking-widest text-white/60 mb-3">Kick-off countdown</p>
      <div className="flex justify-center gap-3">
        {units.map(u => (
          <div key={u.label} className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 min-w-[60px]">
            <span className="text-2xl font-bold block">{String(u.value).padStart(2, '0')}</span>
            <span className="text-[10px] uppercase tracking-wider text-white/60">{u.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Get flag image URL from flagcdn.com
const getFlagUrl = (countryCode) => {
  if (!countryCode || countryCode === 'XX') return null;
  
  // Handle special cases for UK nations and others
  const codeMap = {
    'GB-ENG': 'gb-eng',
    'GB-SCT': 'gb-sct', 
    'GB-WLS': 'gb-wls',
  };
  
  const code = codeMap[countryCode] || countryCode.slice(0, 2).toLowerCase();
  return `https://flagcdn.com/w40/${code}.png`;
};

// Category colors — fixed, readable on any club color
const TIER_COLORS = {
  favourite: { color: '#059669', bg: '#ECFDF5', border: '#A7F3D0', label: 'Favourite' },
  contender: { color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', label: 'Contender' },
  longshot:  { color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE', label: 'Long Shot' },
};

const getTierStyle = (seed) => {
  switch(seed) {
    case 1: return TIER_COLORS.favourite;
    case 2: return TIER_COLORS.contender;
    case 3: case 4: return TIER_COLORS.longshot;
    default: return TIER_COLORS.longshot;
  }
};

function DemoShareBox({ clubName }) {
  const [copied, setCopied] = useState(false);
  const demoUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `Check out this World Cup 2026 Predictor demo for ${clubName}! What do you think?`;

  const handleCopy = () => {
    navigator.clipboard.writeText(demoUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + demoUrl)}`, '_blank');
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: `${clubName} — World Cup 2026 Predictor Demo`, text: shareText, url: demoUrl });
    }
  };

  return (
    <div className="card p-6 mt-8 text-center" data-testid="demo-share-box">
      <ShareNetwork size={28} weight="duotone" className="mx-auto mb-2 text-gray-400" />
      <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Share with your committee</h3>
      <p className="text-sm text-gray-500 mb-5">Send this demo to your team so they can see it too</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={handleWhatsApp}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#25D366] hover:bg-[#1fb855] text-white text-sm font-bold rounded-lg transition-colors"
          data-testid="share-whatsapp-btn"
        >
          <WhatsappLogo size={20} weight="fill" /> WhatsApp
        </button>
        <button
          onClick={handleCopy}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#1D3557] hover:bg-[#15293f] text-white text-sm font-bold rounded-lg transition-colors"
          data-testid="share-copy-btn"
        >
          {copied ? <><Check size={18} weight="bold" /> Copied!</> : <><Copy size={18} weight="bold" /> Copy Link</>}
        </button>
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={handleNativeShare}
            className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-600 text-sm font-bold rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="share-native-btn"
          >
            <ShareNetwork size={18} weight="bold" /> More
          </button>
        )}
      </div>
    </div>
  );
}

// Live leaderboard rendered inline on the club homepage once the tournament
// has kicked off. Shows top 10 by default with an inline "Show all" toggle
// (we render everything; just slice for compact view). Sort order on the server
// is Points DESC, Goals DESC, paid-at ASC — we don't re-sort here so the rank
// numbers are always authoritative.
function LiveLeaderboard({ slug, clubPrimary, textOnPrimary }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/clubs/${slug}/leaderboard`)
      .then(r => { if (!cancelled) { setData(r.data); setError(''); } })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || 'Could not load leaderboard'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const rows = data?.leaderboard || [];
  const visible = showAll ? rows : rows.slice(0, 10);
  const hasMore = rows.length > 10;

  return (
    <section id="live-leaderboard" className="max-w-5xl mx-auto px-6 py-12" data-testid="live-leaderboard">
      <div className="card p-5 sm:p-7">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <h2 className="text-2xl flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary }}>
            <Trophy size={28} weight="duotone" /> Live Leaderboard
          </h2>
          {!loading && rows.length > 0 && (
            <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: clubPrimary, color: textOnPrimary }}>
              {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
            </span>
          )}
        </div>
        {/* Always visible tiebreaker explainer — sits right above the table so
            players don't wonder why two equal-points rows aren't joint-ranked. */}
        <p className="text-xs text-gray-500 mb-4 leading-relaxed" data-testid="leaderboard-tiebreaker-note">
          <strong className="text-gray-700">Tiebreaker:</strong> players on equal points are ranked by the total goals scored by their 6 teams across the tournament (regulation & extra time only — penalty shootouts excluded).
        </p>

        {loading && (
          <div className="py-8 text-center text-gray-400 text-sm">Loading the leaderboard…</div>
        )}
        {!loading && error && (
          <div className="py-6 px-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="py-10 text-center text-gray-400 text-sm">No entries yet.</div>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leaderboard-table">
              <thead>
                <tr className="text-left border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <th className="py-2 pr-3 text-xs font-bold uppercase tracking-wider text-gray-500 w-12">#</th>
                  <th className="py-2 pr-3 text-xs font-bold uppercase tracking-wider text-gray-500">Player</th>
                  <th className="py-2 pr-3 text-xs font-bold uppercase tracking-wider text-gray-500 hidden sm:table-cell">Teams</th>
                  <th className="py-2 pr-3 text-xs font-bold uppercase tracking-wider text-gray-500 text-right">Pts</th>
                  <th className="py-2 pl-3 text-xs font-bold uppercase tracking-wider text-gray-500 text-right" title="Goals scored (tiebreaker)">Goals</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={`${row.rank}-${row.player_name}`} className="border-b last:border-0" style={{ borderColor: 'var(--border-color)' }} data-testid={`leaderboard-row-${row.rank}`}>
                    <td className="py-3 pr-3 font-bold" style={{ color: row.rank <= 3 ? clubPrimary : '#9ca3af' }}>{row.rank}</td>
                    <td className="py-3 pr-3 font-medium text-gray-800 truncate max-w-[160px]">{row.player_name}</td>
                    <td className="py-3 pr-3 hidden sm:table-cell">
                      <div className="flex items-center gap-1 flex-wrap">
                        {(row.teams || []).map((t, i) => (
                          <img
                            key={i}
                            src={getFlagUrl(t.flag_code)}
                            alt={t.name}
                            title={`${t.name}${t.stage ? ` — ${t.stage.replace('_', ' ')}` : ''}${t.eliminated ? ' (eliminated)' : ''}`}
                            className={`w-6 h-4 object-cover rounded-sm ${t.eliminated ? 'opacity-30 grayscale' : ''}`}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="py-3 pr-3 font-black text-right text-base" style={{ color: clubPrimary }}>{row.points}</td>
                    <td className="py-3 pl-3 text-right text-gray-700 font-semibold">{row.goals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowAll(s => !s)}
                  className="text-sm font-bold underline transition-colors"
                  style={{ color: clubPrimary }}
                  data-testid="leaderboard-toggle-all"
                >
                  {showAll ? 'Show top 10 ←' : `View full leaderboard (${rows.length}) →`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default function ClubPredictorPage({ demoMode = false, demoClub = null, demoId = null }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [club, setClub] = useState(demoClub);
  const [teams, setTeams] = useState({});
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [loading, setLoading] = useState(!demoClub);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showTeamSelection, setShowTeamSelection] = useState(false);
  
  // Form state
  const [playerName, setPlayerName] = useState('');
  const [playerEmail, setPlayerEmail] = useState('');
  const [playerPhone, setPlayerPhone] = useState('');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Once we cross kickoff, the homepage transforms: entries closed, live
  // leaderboard becomes the main attraction. Demo sites stay in pre-kickoff
  // mode forever so prospects can still preview the entry flow.
  // `?preview_tournament=1` URL param forces the post-kickoff view on for
  // testing / preview / a-club-wanting-to-see-what-it-will-look-like.
  const forceTournament = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview_tournament') === '1';
  const [tournamentStarted, setTournamentStarted] = useState(() => !demoMode && (forceTournament || Date.now() >= KICKOFF.getTime()));
  // Independent of demoMode — used to flip demo banner CTAs once the real
  // tournament is underway (since `tournamentStarted` is forced false on
  // demos to keep the entry preview functional).
  const worldCupKickedOff = forceTournament || Date.now() >= KICKOFF.getTime();
  const [golfModalOpen, setGolfModalOpen] = useState(false);
  useEffect(() => {
    if (demoMode || tournamentStarted) return;
    const id = setInterval(() => {
      if (Date.now() >= KICKOFF.getTime()) setTournamentStarted(true);
    }, 60000);
    return () => clearInterval(id);
  }, [demoMode, tournamentStarted]);

  useEffect(() => {
    fetchData();
  }, [slug, demoClub]);

  // Toggle body class when the mobile selection bar is visible, so the AI chat
  // bubble can shift up on small screens (see App.css `body.has-mobile-bar`).
  useEffect(() => {
    if (showTeamSelection) {
      document.body.classList.add('has-mobile-bar');
    } else {
      document.body.classList.remove('has-mobile-bar');
    }
    return () => document.body.classList.remove('has-mobile-bar');
  }, [showTeamSelection]);

  const fetchData = async () => {
    try {
      const teamsRes = await axios.get(`${API}/teams/by-group`);
      
      if (!demoMode) {
        const clubRes = await axios.get(`${API}/clubs/${slug}`);
        setClub(clubRes.data);
      } else if (demoClub && !club) {
        setClub(demoClub);
      }
      
      // Process teams: use seed field directly from API
      const processedGroups = {};
      const groups = teamsRes.data.groups || {};
      
      Object.entries(groups).forEach(([groupName, groupTeams]) => {
        processedGroups[groupName] = [...groupTeams].sort((a, b) => a.seed - b.seed);
      });
      
      setTeams(processedGroups);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Club not found');
    } finally {
      setLoading(false);
    }
  };

  // Get all teams flat with their group info
  const getAllTeams = () => {
    const allTeams = [];
    Object.entries(teams).forEach(([group, groupTeams]) => {
      groupTeams.forEach(team => {
        allTeams.push({ ...team, group });
      });
    });
    return allTeams;
  };

  // Get selected teams with full info
  const getSelectedTeamsInfo = () => {
    const allTeams = getAllTeams();
    return selectedTeams.map(id => allTeams.find(t => t.id === id)).filter(Boolean);
  };

  // Count seeds in selection
  const getSeedCounts = () => {
    const selected = getSelectedTeamsInfo();
    return {
      seed1: selected.filter(t => t.seed === 1).length,
      seed2: selected.filter(t => t.seed === 2).length,
      seed3: selected.filter(t => t.seed === 3).length,
      seed4: selected.filter(t => t.seed === 4).length,
    };
  };

  // Get groups that already have a selection
  const getSelectedGroups = () => {
    const selected = getSelectedTeamsInfo();
    return selected.map(t => t.group);
  };

  // Check if a team can be selected
  const canSelectTeam = (team, group) => {
    // Already selected - can deselect
    if (selectedTeams.includes(team.id)) return true;
    
    // Already have 6 teams
    if (selectedTeams.length >= 6) return false;
    
    // Already have a team from this group
    if (getSelectedGroups().includes(group)) return false;
    
    // Check seed constraints: 2 Seed 1, 2 Seed 2, 2 from Seed 3 or 4
    const counts = getSeedCounts();
    if (team.seed === 1 && counts.seed1 >= 2) return false;
    if (team.seed === 2 && counts.seed2 >= 2) return false;
    // Seed 3 and 4 share a combined limit of 2
    if ((team.seed === 3 || team.seed === 4) && (counts.seed3 + counts.seed4) >= 2) return false;
    
    return true;
  };

  const toggleTeam = (teamId, group) => {
    const allTeams = getAllTeams();
    const team = allTeams.find(t => t.id === teamId);
    
    if (selectedTeams.includes(teamId)) {
      setSelectedTeams(selectedTeams.filter(id => id !== teamId));
    } else if (canSelectTeam(team, group)) {
      setSelectedTeams([...selectedTeams, teamId]);
    }
  };

  const seedCounts = getSeedCounts();
  const isValidSelection = selectedTeams.length === 6 && 
    seedCounts.seed1 === 2 && 
    seedCounts.seed2 === 2 && 
    (seedCounts.seed3 + seedCounts.seed4) === 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidSelection) {
      setError('Please select 2 Favourites, 2 Contenders, and 2 Long Shots (one per group)');
      return;
    }

    // Demo mode: redirect to onboard form
    if (demoMode) {
      const params = new URLSearchParams({
        club_name: club.name || '',
        primary_colour: club.primary_color || '',
        secondary_colour: club.secondary_color || '',
        currency: club.currency || 'EUR',
        entry_fee: club.entry_fee || 10,
      });
      if (club.logo_url) params.set('logo_url', club.logo_url);
      navigate(`/onboard?${params.toString()}`);
      return;
    }
    
    if (!playerName || !playerEmail) {
      setError('Please fill in your name and email');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API}/clubs/${slug}/entries`, {
        player_name: playerName,
        player_email: playerEmail,
        player_phone: playerPhone,
        team_picks: selectedTeams
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Error submitting entry:', err);
      setError(err.response?.data?.detail || 'Failed to submit entry');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-main)' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (error === 'Club not found' || !club) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-main)' }}>
        <div className="text-center">
          <h1 className="text-2xl mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Club Not Found</h1>
          <button onClick={() => navigate('/')} className="btn-secondary">
            <ArrowLeft size={20} className="inline mr-2" /> Back
          </button>
        </div>
      </div>
    );
  }

  // Coming Soon page for clubs not yet live (skip in demo mode)
  if (!demoMode && !club.is_live) {
    const clubPrimaryCS = club.primary_color || '#1D3557';
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: `linear-gradient(135deg, ${clubPrimaryCS}08, ${clubPrimaryCS}15)` }}>
        <div className="text-center max-w-md">
          {club.logo_url && (
            <img src={assetUrl(club.logo_url)} alt={club.name} className="w-24 h-24 mx-auto object-contain mb-6" />
          )}
          <h1 className="text-4xl font-black uppercase tracking-tight mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimaryCS }}>
            {club.name}
          </h1>
          <p className="text-lg font-bold text-gray-600 mb-2">World Cup 2026 Predictor</p>
          <div className="inline-block bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium px-5 py-3 rounded-lg mt-4">
            Coming Soon
          </div>
          <p className="text-gray-400 text-sm mt-4">We're getting everything ready. Check back shortly!</p>
          <div className="mt-8">
            <a
              href="https://www.targetfunder.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <img src="https://www.targetfunder.com/tf_logo.png" alt="TF" className="h-6 w-auto object-contain" />
              <span className="font-heading font-black text-base uppercase tracking-tighter leading-none text-gray-400">
                Powered by <span style={{ color: '#1D3557' }}>target</span><span style={{ color: '#E63946' }}>funder</span><span style={{ color: '#1D3557', opacity: 0.3 }}>.com</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  const clubPrimary = club.primary_color || '#1D428A';
  const textOnPrimary = getContrastColor(clubPrimary);

  // Welcome/Rules page — also where the post-kickoff tournament view lives.
  // We force the welcome view (and prevent users from drilling into team
  // selection) once entries close.
  if (!showTeamSelection || tournamentStarted) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--bg-main)' }}>
        {/* Demo Banner */}
        {demoMode && (
          <div className="sticky top-0 z-[60] bg-[#1D3557] text-white px-6 py-5" data-testid="demo-banner">
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
              {worldCupKickedOff ? (
                <>
                  <div className="flex items-center gap-4">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-[#E63946] px-3 py-3 shrink-0 leading-none">Demo</span>
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight">The World Cup has started — entries closed</p>
                      <p className="text-xs sm:text-sm text-white/60">Our <strong className="text-white">Open Golf 2026</strong> predictor launches before The Open — get a branded demo for <strong className="text-white">{club.name}</strong></p>
                    </div>
                  </div>
                  <button
                    onClick={() => setGolfModalOpen(true)}
                    className="bg-[#E63946] hover:bg-[#d32f3c] text-white text-sm font-bold uppercase tracking-widest px-8 py-3 transition-colors shrink-0 text-center"
                    data-testid="demo-golf-waitlist-btn"
                  >
                    <span>Notify me about Open Golf →</span>
                    <span className="block text-xs font-bold normal-case tracking-normal text-white mt-1">Free demo · No card needed</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-[#E63946] px-3 py-3 shrink-0 leading-none">Demo</span>
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight">Like what you see?</p>
                      <p className="text-xs sm:text-sm text-white/60">Activate to start raising funds for <strong className="text-white">{club.name}</strong></p>
                    </div>
                  </div>
                  <a
                    href={`/onboard?club_name=${encodeURIComponent(club.name)}&primary_colour=${encodeURIComponent(club.primary_color)}&secondary_colour=${encodeURIComponent(club.secondary_color)}&currency=${encodeURIComponent(club.currency || 'EUR')}&entry_fee=${club.entry_fee || 10}${club.logo_url ? `&logo_url=${encodeURIComponent(club.logo_url)}` : ''}`}
                    className="bg-[#E63946] hover:bg-[#d32f3c] text-white text-sm font-bold uppercase tracking-widest px-8 py-3 transition-colors shrink-0 text-center"
                    data-testid="demo-activate-btn"
                  >
                    <span>Activate from {club.currency === 'GBP' ? '£0' : '€0'}</span>
                    <span className="block text-xs font-bold normal-case tracking-normal text-white mt-1">Most clubs raise {(club.currency === 'GBP' ? '£' : '€')}3,000+</span>
                  </a>
                </>
              )}
            </div>
          </div>
        )}
        {demoMode && <DemoWatermark />}
        {demoMode && <GolfWaitlistModal open={golfModalOpen} onClose={() => setGolfModalOpen(false)} defaultClubName={club.name} />}
        {/* Header */}
        <header className="glass-header sticky top-0 z-50 px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {club.logo_url ? (
                <img src={assetUrl(club.logo_url)} alt={club.name} className="w-10 h-10 object-contain" />
              ) : (
                <div 
                  className="w-10 h-10 flex items-center justify-center font-bold rounded"
                  style={{ background: clubPrimary, color: textOnPrimary }}
                >
                  {club.name.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary }} data-testid="club-name">
                  {club.name}
                </h1>
                <p className="text-xs text-gray-500">World Cup 2026 Predictor</p>
              </div>
            </div>
            {/* Sticky Leaderboard jump button — only during tournament so users
                on long pages can hop straight to the live action. */}
            {tournamentStarted && (
              <a
                href="#live-leaderboard"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('live-leaderboard')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider transition-transform hover:scale-105"
                style={{ background: clubPrimary, color: textOnPrimary }}
                data-testid="header-leaderboard-jump"
              >
                <Trophy size={16} weight="fill" /> Leaderboard
              </a>
            )}
          </div>
        </header>

        {/* Announcement Banner */}
        {club.announcement_banner && (
          <div
            className="w-full text-center text-sm sm:text-base py-3 px-4 font-medium"
            style={{ background: clubPrimary, color: textOnPrimary }}
            data-testid="club-announcement-banner"
          >
            {club.announcement_banner}
          </div>
        )}

        {/* Hero Section */}
        <div className="welcome-hero py-16 px-6" style={{ backgroundColor: club.primary_color || '#1a1a2e' }}>
          <div className="max-w-5xl mx-auto text-center relative z-10">
            {(() => {
              const sec = club.secondary_color || '#ffffff';
              const ringStyle = {
                borderWidth: '4px',
                borderStyle: 'solid',
                borderColor: sec,
                // shadow-2xl + secondary-tinted outer ring
                boxShadow: `0 0 0 6px ${hexToRgba(sec, 0.3)}, 0 25px 50px -12px rgba(0,0,0,0.25)`,
              };
              return (
                <div className="flex items-center justify-center gap-6 mb-6">
                  {club.logo_url && (
                    <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-white overflow-hidden flex items-center justify-center" style={ringStyle}>
                      <img
                        src={assetUrl(club.logo_url)}
                        alt={club.name}
                        className="w-[72%] h-[72%] object-contain"
                        // Ref runs synchronously after mount and catches images
                        // that loaded from cache before React could attach onLoad.
                        ref={(el) => {
                          if (!el) return;
                          const apply = () => {
                            const w = el.naturalWidth;
                            const h = el.naturalHeight;
                            if (!w || !h) return;
                            const ratio = w / h;
                            if (ratio > 0.92 && ratio < 1.08) {
                              el.className = 'w-full h-full object-cover';
                            }
                          };
                          if (el.complete && el.naturalWidth) apply();
                          else el.onload = apply;
                        }}
                      />
                    </div>
                  )}
                  <div className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden" style={ringStyle}>
                    <img
                      src={WORLD_CUP_TROPHY}
                      alt="World Cup Trophy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              );
            })()}
            <p className="text-sm uppercase tracking-widest text-white/70 mb-2">FIFA World Cup 2026</p>
            <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {club.name} Predictor
            </h1>
            {tournamentStarted ? (
              <>
                <div className="inline-block bg-white/15 backdrop-blur-sm rounded-lg px-6 py-3 border-2 border-white/30 mb-4" data-testid="tournament-started-badge">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/70">Entries Closed</p>
                  <p className="text-2xl font-black mt-0.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>The Tournament Has Started</p>
                </div>
                <p className="text-base sm:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed">
                  See the live leaderboard below. Check your teams&rsquo; progress after each round.
                </p>
              </>
            ) : (
              <>
                <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                  Pick your 6 teams and compete for the top of the leaderboard!
                </p>
                <div className="inline-block bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
                  <span className="text-white/70">Entry Fee:</span>
                  <span className="text-2xl font-bold ml-2">{cs(club.currency)}{club.entry_fee?.toFixed(2)}</span>
                </div>
                <Countdown />
              </>
            )}
          </div>
        </div>

        {/* Live leaderboard — rendered immediately under hero during the
            tournament so it's the first thing players see when they land. */}
        {tournamentStarted && (
          <LiveLeaderboard slug={slug} clubPrimary={clubPrimary} textOnPrimary={textOnPrimary} />
        )}

        {/* Rules Section — collapsed to an accordion during the tournament so
            the leaderboard owns the page; full panel before kickoff. */}
        <div className="max-w-5xl mx-auto px-6 py-12">
          {tournamentStarted ? (
            <details className="card p-5 mb-8 group" data-testid="tournament-rules-accordion">
              <summary className="cursor-pointer text-base font-semibold flex items-center justify-between gap-3 list-none" style={{ color: clubPrimary }}>
                <span className="flex items-center gap-2">
                  <Info size={20} weight="duotone" /> How scoring works
                </span>
                <span className="text-xs text-gray-400 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <div className="pt-5 mt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <p className="text-sm text-gray-600 mb-4">
                  Players picked 6 teams pre-tournament — 2 Favourites, 2 Contenders, 2 Long Shots. Points are awarded for how far each team progresses; later stages replace earlier stage points (non-cumulative).
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  {POINTS_SYSTEM.map(p => (
                    <div key={p.stage} className="bg-gray-50 rounded p-2 text-center">
                      <p className="font-bold text-gray-700">{p.stage}</p>
                      <p className="font-black text-lg" style={{ color: clubPrimary }}>{p.points} pt{p.points === 1 ? '' : 's'}</p>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          ) : (
          <div className="card p-8 mb-8">
            <h2 className="text-2xl mb-6 pb-3 flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary, borderBottom: `2px solid ${club.secondary_color || '#e5e7eb'}` }}>
              <Info size={28} weight="duotone" /> How It Works
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3" style={{ background: clubPrimary, color: textOnPrimary }}>1</div>
                <h3 className="font-bold mb-2">Pick 6 Teams</h3>
                <p className="text-gray-600 text-sm">Choose <strong>one team per group</strong> from 6 different groups</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3" style={{ background: clubPrimary, color: textOnPrimary }}>2</div>
                <h3 className="font-bold mb-2">Follow the Rules</h3>
                <p className="text-gray-600 text-sm">Pick <strong style={{ color: TIER_COLORS.favourite.color }}>2 Favourites</strong>, <strong style={{ color: TIER_COLORS.contender.color }}>2 Contenders</strong>, and <strong style={{ color: TIER_COLORS.longshot.color }}>2 Long Shots</strong></p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3" style={{ background: clubPrimary, color: textOnPrimary }}>3</div>
                <h3 className="font-bold mb-2">Earn Points</h3>
                <p className="text-gray-600 text-sm">Score points as your teams progress through each round</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-600">
                Help <strong>{club.name}</strong> raise funds — the player with the most points at the end wins!
              </p>
              <p className="text-gray-500 text-sm mt-2">
                <strong>Each team combination can only be picked once.</strong> Pick yours before someone else does!
              </p>
            </div>
          </div>
          )}

          {/* Prizes Section */}
          {((club.prizes && club.prizes.length > 0) || demoMode) && (() => {
            const prizesToShow = demoMode ? DEMO_PRIZES : club.prizes;
            const singular = prizesToShow.length === 1;
            return (
            <div className="card p-8 mb-8">
              <h2 className={`mb-6 pb-3 flex items-center gap-3 ${singular ? 'text-3xl justify-center' : 'text-2xl'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary, borderBottom: `2px solid ${club.secondary_color || '#e5e7eb'}` }}>
                <Star size={singular ? 32 : 28} weight="duotone" /> {singular ? 'Prize' : 'Prizes'}
              </h2>
              {demoMode && (
                <p className="text-xs text-gray-400 italic mb-4">Example prizes — you'll set your own when you activate</p>
              )}
              <div className={singular ? 'flex justify-center' : 'grid md:grid-cols-3 gap-6'}>
                {prizesToShow.map((prize, index) => {
                  const title = prize.title || prize.name || '';
                  const sponsor = prize.sponsor_name || prize.sponsor || '';
                  return (
                  <div key={index} className={`text-center rounded-lg bg-white ${singular ? 'p-10 sm:p-14 w-full max-w-xl' : 'p-6'}`} style={{ border: '1px solid var(--border-color)' }}>
                    <div className={`font-bold mb-2 ${singular ? 'text-5xl' : 'text-3xl'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif', color: index === 0 ? clubPrimary : 'inherit' }}>
                      {title}
                    </div>
                    <div className={`font-medium text-gray-700 mb-4 ${singular ? 'text-2xl leading-snug' : 'text-lg'}`}>{prize.description}</div>
                    {prize.sponsor_logo_url && (
                      <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                        <p className="text-xs text-gray-400 mb-3">Sponsored by</p>
                        <img src={assetUrl(prize.sponsor_logo_url)} alt={sponsor || 'Sponsor'} className={`mx-auto object-contain mb-2 ${singular ? 'h-40' : 'h-32'}`} />
                        {sponsor && <p className={`font-bold text-gray-700 ${singular ? 'text-lg' : 'text-base'}`}>{sponsor}</p>}
                      </div>
                    )}
                    {sponsor && !prize.sponsor_logo_url && (
                      <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                        <p className="text-xs text-gray-400 mb-1">Sponsored by</p>
                        <p className={`font-bold text-gray-700 ${singular ? 'text-xl' : 'text-lg'}`}>{sponsor}</p>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {/* Points System — hidden during tournament; the accordion above
              + leaderboard column already explain scoring. */}
          {!tournamentStarted && (
          <div className="card p-8 mb-8">
            <h2 className="text-2xl mb-6 pb-3 flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary, borderBottom: `2px solid ${club.secondary_color || '#e5e7eb'}` }}>
              <Trophy size={28} weight="duotone" /> Points System
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {POINTS_SYSTEM.map((item, index) => (
                <div key={index} className="stage-card">
                  <div className="stage-points" style={{ color: clubPrimary }}>{item.points}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mt-1">{item.stage}</div>
                </div>
              ))}
            </div>
            
            <p className="text-center text-gray-500 text-sm mt-6">
              <strong>Tiebreaker:</strong> If players are tied on points, the total goals scored by their teams (excluding penalty shootouts) decides the winner
            </p>
          </div>
          )}

          {/* CTA — only before kickoff; entries are closed during the tournament. */}
          {!tournamentStarted && (
          <div className="text-center">
            <button
              onClick={() => { setShowTeamSelection(true); window.scrollTo(0, 0); }}
              className="btn-primary text-lg px-12 py-4 flex items-center gap-3 mx-auto"
              style={{ background: clubPrimary, borderColor: clubPrimary, color: textOnPrimary }}
              data-testid="start-picking-btn"
            >
              Start Picking Your Teams <ArrowRight size={20} weight="bold" />
            </button>
          </div>
          )}

          {/* Demo Share Section */}
          {demoMode && <DemoShareBox clubName={club.name} />}
        </div>

        {/* Footer */}
        <footer className="py-6 px-6 border-t text-center" style={{ borderColor: 'var(--border-color)' }}>
          <p className="text-gray-500 text-sm">Supporting {club.name} through World Cup 2026</p>
          <p className="text-gray-400 text-xs mt-2">*Less standard payment processing fees</p>
          <button onClick={() => demoMode ? window.open('https://www.targetfunder.com/terms', '_blank') : navigate(`/worldcup2026/${slug}/terms`)} className="text-gray-400 text-xs mt-2 hover:text-gray-600 transition-colors underline" data-testid="terms-link">Terms & Conditions</button>
          <span className="text-gray-300 text-xs mt-2 mx-2">|</span>
          <button onClick={() => demoMode ? window.open('https://www.targetfunder.com/terms', '_blank') : navigate(`/worldcup2026/${slug}/privacy`)} className="text-gray-400 text-xs mt-2 hover:text-gray-600 transition-colors underline" data-testid="privacy-link">Privacy Notice</button>
          
          {/* TargetFunder badge */}
          <div className="mt-5 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <a
              href="https://www.targetfunder.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200"
              data-testid="targetfunder-badge"
            >
              <img src="https://www.targetfunder.com/tf_logo.png" alt="TargetFunder" className="h-6 w-auto object-contain" />
              <span className="font-heading font-black text-base uppercase tracking-tighter leading-none text-gray-500">
                Made by <span style={{ color: '#1D3557' }}>target</span><span style={{ color: '#E63946' }}>funder</span><span style={{ color: '#1D3557', opacity: 0.3 }}>.com</span>
              </span>
            </a>
          </div>
        </footer>
      </div>
    );
  }

  // Team Selection Page
  const selectedGroups = getSelectedGroups();
  const selectedInfo = getSelectedTeamsInfo();

  return (
    <div className="min-h-screen pb-44 lg:pb-0" style={{ background: 'var(--bg-main)' }}>
      {/* Demo Banner */}
      {demoMode && (
        <div className="sticky top-0 z-[60] bg-[#1D3557] text-white px-6 py-5" data-testid="demo-banner-selection">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            {worldCupKickedOff ? (
              <>
                <div className="flex items-center gap-4">
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-[#E63946] px-3 py-3 shrink-0 leading-none">Demo</span>
                  <div>
                    <p className="text-base sm:text-lg font-bold leading-tight">The World Cup has started — entries closed</p>
                    <p className="text-xs sm:text-sm text-white/60">Our <strong className="text-white">Open Golf 2026</strong> predictor launches before The Open — get a branded demo for <strong className="text-white">{club.name}</strong></p>
                  </div>
                </div>
                <button
                  onClick={() => setGolfModalOpen(true)}
                  className="bg-[#E63946] hover:bg-[#d32f3c] text-white text-sm font-bold uppercase tracking-widest px-8 py-3 transition-colors shrink-0 text-center"
                  data-testid="demo-golf-waitlist-btn-selection"
                >
                  <span>Notify me about Open Golf →</span>
                  <span className="block text-xs font-bold normal-case tracking-normal text-white mt-1">Free demo · No card needed</span>
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-4">
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-[#E63946] px-3 py-3 shrink-0 leading-none">Demo</span>
                  <div>
                    <p className="text-base sm:text-lg font-bold leading-tight">Go on, try picking your teams!</p>
                    <p className="text-xs sm:text-sm text-white/60">This is exactly how it works for <strong className="text-white">{club.name}</strong> members</p>
                  </div>
                </div>
                <a
                  href={`/onboard?club_name=${encodeURIComponent(club.name)}&primary_colour=${encodeURIComponent(club.primary_color)}&secondary_colour=${encodeURIComponent(club.secondary_color)}&currency=${encodeURIComponent(club.currency || 'EUR')}&entry_fee=${club.entry_fee || 10}${club.logo_url ? `&logo_url=${encodeURIComponent(club.logo_url)}` : ''}`}
                  className="bg-[#E63946] hover:bg-[#d32f3c] text-white text-sm font-bold uppercase tracking-widest px-8 py-3 transition-colors shrink-0 text-center"
                >
                  <span>Activate from {club.currency === 'GBP' ? '£0' : '€0'}</span>
                  <span className="block text-xs font-bold normal-case tracking-normal text-white mt-1">Most clubs raise {(club.currency === 'GBP' ? '£' : '€')}3,000+</span>
                </a>
              </>
            )}
          </div>
        </div>
      )}
      {demoMode && <DemoWatermark />}
      {demoMode && <GolfWaitlistModal open={golfModalOpen} onClose={() => setGolfModalOpen(false)} defaultClubName={club.name} />}
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <button 
              onClick={() => setShowTeamSelection(false)} 
              className="text-gray-400 hover:text-gray-800 transition-colors"
              data-testid="back-to-rules-btn"
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center gap-3">
              {club.logo_url ? (
                <img src={assetUrl(club.logo_url)} alt={club.name} className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              ) : (
                <div 
                  className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center font-bold rounded text-sm"
                  style={{ background: clubPrimary, color: textOnPrimary }}
                >
                  {club.name.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-base sm:text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary }} data-testid="club-name-selection">
                  {club.name}
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block">Select Your Teams</p>
              </div>
            </div>
          </div>
          
          {/* Desktop: category counters in header */}
          <div className="hidden sm:flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: TIER_COLORS.favourite.bg, color: TIER_COLORS.favourite.color, border: `1px solid ${TIER_COLORS.favourite.border}` }}>Fav {seedCounts.seed1}/2</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: TIER_COLORS.contender.bg, color: TIER_COLORS.contender.color, border: `1px solid ${TIER_COLORS.contender.border}` }}>Con {seedCounts.seed2}/2</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: TIER_COLORS.longshot.bg, color: TIER_COLORS.longshot.color, border: `1px solid ${TIER_COLORS.longshot.border}` }}>LS {seedCounts.seed3 + seedCounts.seed4}/2</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-40 lg:pb-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Team Selection */}
          <div className="lg:col-span-2">
            <div className="mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl mb-1 sm:mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary }} data-testid="select-teams-title">
                Select Your 6 Teams
              </h2>
              <p className="text-gray-600 text-sm sm:text-base">
                Pick <strong>one team per group</strong> from 6 different groups. 
                You must select <strong style={{ color: TIER_COLORS.favourite.color }}>2 Favourites</strong>, <strong style={{ color: TIER_COLORS.contender.color }}>2 Contenders</strong>, and <strong style={{ color: TIER_COLORS.longshot.color }}>2 Long Shots</strong>.
              </p>
            </div>

            {/* Groups Grid */}
            <div className="space-y-4 sm:space-y-6">
              {Object.entries(teams).sort().map(([group, groupTeams]) => {
                const hasSelection = selectedGroups.includes(group);
                
                return (
                  <div key={group} className={`card p-3 sm:p-4 ${hasSelection ? 'ring-2' : ''}`} style={hasSelection ? { ringColor: clubPrimary } : {}}>
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="overline">Group {group}</h3>
                      {hasSelection && <Check size={18} weight="bold" style={{ color: clubPrimary }} />}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                      {groupTeams.map(team => {
                        const isSelected = selectedTeams.includes(team.id);
                        const canSelect = canSelectTeam(team, group);
                        const tier = getTierStyle(team.seed);
                        const isDisabled = !canSelect && !isSelected;
                        
                        return (
                          <div
                            key={team.id}
                            onClick={() => !isDisabled && toggleTeam(team.id, group)}
                            className={`team-card ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                            data-testid={`team-card-${team.country_code}`}
                            style={{ 
                              background: tier.bg, 
                              borderColor: isSelected ? tier.color : tier.border,
                              borderWidth: isSelected ? '2.5px' : '1px',
                              boxShadow: isSelected ? `0 0 0 1px ${tier.color}` : 'none'
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <img 
                                  src={getFlagUrl(team.country_code)} 
                                  alt={team.name}
                                  className="w-6 h-4 object-cover rounded-sm shadow-sm"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                                <span className="font-medium text-sm">{team.name}</span>
                              </div>
                              {isSelected && <Check size={16} weight="bold" style={{ color: tier.color }} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Entry Form Sidebar — DESKTOP ONLY */}
          <div className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24 h-[calc(100vh-7rem)] overflow-y-auto pr-1 space-y-6">
              <div className="card p-5">
                <h3 className="text-xl mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary }}>Your Selection</h3>
                
                {/* Category Requirements */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className={`text-center p-1.5 rounded ${seedCounts.seed1 === 2 ? 'ring-1 ring-green-400' : ''}`} style={{ background: TIER_COLORS.favourite.bg, border: `1px solid ${TIER_COLORS.favourite.border}` }}>
                    <span className="text-xs font-bold block" style={{ color: TIER_COLORS.favourite.color }}>Fav</span>
                    <div className="text-sm font-bold">{seedCounts.seed1}/2</div>
                  </div>
                  <div className={`text-center p-1.5 rounded ${seedCounts.seed2 === 2 ? 'ring-1 ring-green-400' : ''}`} style={{ background: TIER_COLORS.contender.bg, border: `1px solid ${TIER_COLORS.contender.border}` }}>
                    <span className="text-xs font-bold block" style={{ color: TIER_COLORS.contender.color }}>Con</span>
                    <div className="text-sm font-bold">{seedCounts.seed2}/2</div>
                  </div>
                  <div className={`text-center p-1.5 rounded ${(seedCounts.seed3 + seedCounts.seed4) === 2 ? 'ring-1 ring-green-400' : ''}`} style={{ background: TIER_COLORS.longshot.bg, border: `1px solid ${TIER_COLORS.longshot.border}` }}>
                    <span className="text-xs font-bold block" style={{ color: TIER_COLORS.longshot.color }}>LS</span>
                    <div className="text-sm font-bold">{seedCounts.seed3 + seedCounts.seed4}/2</div>
                  </div>
                </div>

                {/* Selected Teams */}
                <div className="space-y-1.5 mb-3">
                  {selectedInfo.map((team) => {
                    const tier = getTierStyle(team.seed);
                    return (
                      <div 
                        key={team.id} 
                        className="flex items-center justify-between px-3 py-1.5 rounded"
                        style={{ background: tier.bg, border: `1px solid ${tier.border}` }}
                      >
                        <div className="flex items-center gap-2">
                          <img 
                            src={getFlagUrl(team.country_code)} 
                            alt={team.name}
                            className="w-6 h-4 object-cover rounded-sm"
                          />
                          <span className="font-medium text-sm">{team.name}</span>
                        </div>
                        <span className="text-xs font-bold" style={{ color: tier.color }}>
                          {tier.label}
                        </span>
                      </div>
                    );
                  })}
                  {[...Array(Math.max(0, 6 - selectedTeams.length))].map((_, i) => (
                    <div 
                      key={`empty-${i}`} 
                      className="flex items-center justify-between px-3 py-1.5 rounded"
                      style={{ border: '1px dashed var(--border-color)' }}
                    >
                      <span className="text-gray-400 text-sm">Pick team {selectedTeams.length + i + 1}</span>
                    </div>
                  ))}
                </div>

                {/* Validation Status */}
                <div className="border-t pt-3" style={{ borderColor: 'var(--border-color)' }}>
                  {isValidSelection ? (
                    <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--success)' }}>
                      <Check size={18} weight="bold" />
                      <span>Selection complete!</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Warning size={18} />
                      <span>Pick 2 Fav, 2 Con, 2 Long Shot</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Entry Form */}
              <form onSubmit={handleSubmit} className="card p-5">
                <h3 className="text-xl mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary }}>Your Details</h3>
                
                {/* Entry form - demo shows static placeholders, live shows real inputs */}
                {demoMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Name *</label>
                    <div className="input-field w-full text-gray-400 italic bg-gray-50 cursor-default">Entrant puts name here</div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Email *</label>
                    <div className="input-field w-full text-gray-400 italic bg-gray-50 cursor-default">Entrant puts email here</div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Phone (optional)</label>
                    <div className="input-field w-full text-gray-400 italic bg-gray-50 cursor-default">Entrant puts phone here</div>
                  </div>
                </div>
                ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Name *</label>
                    <input
                      type="text"
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      className="input-field w-full"
                      placeholder="Your full name"
                      required
                      data-testid="player-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Email *</label>
                    <input
                      type="email"
                      value={playerEmail}
                      onChange={(e) => setPlayerEmail(e.target.value)}
                      className="input-field w-full"
                      placeholder="your@email.com"
                      required
                      data-testid="player-email-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Phone (optional)</label>
                    <input
                      type="tel"
                      value={playerPhone}
                      onChange={(e) => setPlayerPhone(e.target.value)}
                      className="input-field w-full"
                      placeholder="+44 7xxx xxxxxx"
                      data-testid="player-phone-input"
                    />
                  </div>
                </div>
                )}

                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded" data-testid="error-message">
                    {error}
                  </div>
                )}

                {demoMode ? (
                  <div className="mt-6 w-full">
                    <button
                      disabled
                      className="btn-primary w-full flex items-center justify-center gap-2 cursor-default"
                      style={{ background: '#FF7F50', borderColor: '#FF7F50', color: '#FFFFFF' }}
                      data-testid="demo-pay-btn"
                    >
                      <Trophy size={20} weight="bold" />
                      Entrants pay {cs(club.currency)}{club.entry_fee?.toFixed(2)} here
                    </button>
                    <p className="text-xs text-gray-400 text-center mt-2">This button goes live when you activate</p>
                  </div>
                ) : (
                <button
                  type="submit"
                  disabled={!isValidSelection || submitting || !club.has_stripe}
                  className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
                  style={{ background: clubPrimary, borderColor: clubPrimary, color: textOnPrimary }}
                  data-testid="submit-entry-btn"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: textOnPrimary, borderTopColor: 'transparent' }} />
                      Processing...
                    </>
                  ) : !club.has_stripe ? (
                    club.stripe_pending_review ? 'Payments Under Review' : 'Payments Coming Soon'
                  ) : (
                    <>
                      <Trophy size={20} weight="bold" />
                      Pay {cs(club.currency)}{club.entry_fee?.toFixed(2)} & Enter
                    </>
                  )}
                </button>
                )}
                
                {!demoMode && (
                <p className="text-xs text-gray-500 text-center mt-4">
                  Your entry helps {club.name} raise funds. Good luck!
                </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer on team selection page */}
      <footer className="py-6 px-6 border-t text-center mt-8" style={{ borderColor: 'var(--border-color)' }}>
        <p className="text-gray-400 text-xs">*Less standard payment processing fees</p>
        <div className="mt-3 flex items-center justify-center gap-4">
          <button onClick={() => demoMode ? window.open('https://www.targetfunder.com/terms', '_blank') : navigate(`/worldcup2026/${slug}/terms`)} className="text-gray-400 text-xs hover:text-gray-600 transition-colors underline" data-testid="selection-terms-link">Terms & Conditions</button>
          <span className="text-gray-300 text-xs">|</span>
          <button onClick={() => demoMode ? window.open('https://www.targetfunder.com/terms', '_blank') : navigate(`/worldcup2026/${slug}/privacy`)} className="text-gray-400 text-xs hover:text-gray-600 transition-colors underline" data-testid="selection-privacy-link">Privacy Notice</button>
        </div>
        <div className="mt-4">
          <a
            href="https://www.targetfunder.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200"
            data-testid="selection-targetfunder-badge"
          >
            <img src="https://www.targetfunder.com/tf_logo.png" alt="TargetFunder" className="h-6 w-auto object-contain" />
            <span className="font-heading font-black text-base uppercase tracking-tighter leading-none text-gray-500">
              Made by <span style={{ color: '#1D3557' }}>target</span><span style={{ color: '#E63946' }}>funder</span><span style={{ color: '#1D3557', opacity: 0.3 }}>.com</span>
            </span>
          </a>
        </div>
      </footer>

      {/* MOBILE STICKY BOTTOM BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden" data-testid="mobile-bottom-bar">
        {/* Expanded drawer */}
        {mobileDrawerOpen && (
          <div className="bg-white border-t shadow-2xl rounded-t-2xl max-h-[80vh] overflow-y-auto" style={{ borderColor: 'var(--border-color)' }}>
            <div className="sticky top-0 bg-white z-10 px-4 pt-3 pb-2 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="font-bold text-base" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: clubPrimary }}>Your Selection</h3>
              <button onClick={() => setMobileDrawerOpen(false)} className="p-1 text-gray-400" data-testid="close-mobile-drawer">
                <X size={20} />
              </button>
            </div>

            <div className="px-4 py-3">
              {/* Category counters */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className={`text-center p-2 rounded ${seedCounts.seed1 === 2 ? 'ring-1 ring-green-400' : ''}`} style={{ background: TIER_COLORS.favourite.bg, border: `1px solid ${TIER_COLORS.favourite.border}` }}>
                  <span className="text-xs font-bold" style={{ color: TIER_COLORS.favourite.color }}>Fav</span>
                  <div className="text-sm font-bold mt-1">{seedCounts.seed1}/2</div>
                </div>
                <div className={`text-center p-2 rounded ${seedCounts.seed2 === 2 ? 'ring-1 ring-green-400' : ''}`} style={{ background: TIER_COLORS.contender.bg, border: `1px solid ${TIER_COLORS.contender.border}` }}>
                  <span className="text-xs font-bold" style={{ color: TIER_COLORS.contender.color }}>Con</span>
                  <div className="text-sm font-bold mt-1">{seedCounts.seed2}/2</div>
                </div>
                <div className={`text-center p-2 rounded ${(seedCounts.seed3 + seedCounts.seed4) === 2 ? 'ring-1 ring-green-400' : ''}`} style={{ background: TIER_COLORS.longshot.bg, border: `1px solid ${TIER_COLORS.longshot.border}` }}>
                  <span className="text-xs font-bold" style={{ color: TIER_COLORS.longshot.color }}>LS</span>
                  <div className="text-sm font-bold mt-1">{seedCounts.seed3 + seedCounts.seed4}/2</div>
                </div>
              </div>

              {/* Selected teams list */}
              <div className="space-y-2 mb-4">
                {selectedInfo.map((team) => {
                  const tier = getTierStyle(team.seed);
                  return (
                    <div key={team.id} className="flex items-center justify-between p-2.5 rounded" style={{ background: tier.bg, border: `1px solid ${tier.border}` }}>
                      <div className="flex items-center gap-2">
                        <img src={getFlagUrl(team.country_code)} alt={team.name} className="w-6 h-4 object-cover rounded-sm" />
                        <span className="font-medium text-sm">{team.name}</span>
                        <span className="text-xs text-gray-400">Grp {team.group}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold" style={{ color: tier.color }}>
                          {tier.label}
                        </span>
                        <button
                          onClick={() => toggleTeam(team.id, team.group)}
                          className="text-gray-400 hover:text-red-500 p-0.5"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {selectedTeams.length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-2">No teams selected yet</p>
                )}
              </div>

              {/* Form fields */}
              {isValidSelection && (
                <form onSubmit={handleSubmit}>
                  <div className="space-y-3 mb-3">
                    {demoMode ? (
                      <>
                        <div className="input-field w-full text-sm text-gray-400 italic bg-gray-50 cursor-default">Entrant puts name here</div>
                        <div className="input-field w-full text-sm text-gray-400 italic bg-gray-50 cursor-default">Entrant puts email here</div>
                        <div className="input-field w-full text-sm text-gray-400 italic bg-gray-50 cursor-default">Entrant puts phone here</div>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={playerName}
                          onChange={(e) => setPlayerName(e.target.value)}
                          className="input-field w-full text-sm"
                          placeholder="Your full name *"
                          required
                          data-testid="mobile-player-name-input"
                        />
                        <input
                          type="email"
                          value={playerEmail}
                          onChange={(e) => setPlayerEmail(e.target.value)}
                          className="input-field w-full text-sm"
                          placeholder="your@email.com *"
                          required
                          data-testid="mobile-player-email-input"
                        />
                        <input
                          type="tel"
                          value={playerPhone}
                          onChange={(e) => setPlayerPhone(e.target.value)}
                          className="input-field w-full text-sm"
                          placeholder="Phone (optional)"
                          data-testid="mobile-player-phone-input"
                        />
                      </>
                    )}
                  </div>

                  {error && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-600 text-xs rounded" data-testid="mobile-error-message">
                      {error}
                    </div>
                  )}

                  {demoMode ? (
                    <button
                      disabled
                      className="btn-primary w-full flex items-center justify-center gap-2 cursor-default"
                      style={{ background: '#FF7F50', borderColor: '#FF7F50', color: '#FFFFFF' }}
                    >
                      <Trophy size={18} weight="bold" />
                      Entrants pay {cs(club.currency)}{club.entry_fee?.toFixed(2)} here
                    </button>
                  ) : (
                  <button
                    type="submit"
                    disabled={submitting || !club.has_stripe}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    style={{ background: clubPrimary, borderColor: clubPrimary, color: textOnPrimary }}
                    data-testid="mobile-submit-entry-btn"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} />
                        Processing...
                      </>
                    ) : !club.has_stripe ? (
                      'Payments Coming Soon'
                    ) : (
                      <>
                        <Trophy size={18} weight="bold" />
                        Pay {cs(club.currency)}{club.entry_fee?.toFixed(2)} & Enter
                      </>
                    )}
                  </button>
                  )}
                </form>
              )}
            </div>
          </div>
        )}

        {/* Collapsed bar — category slots */}
        {!mobileDrawerOpen && (
          <div 
            className="bg-white border-t shadow-lg px-3 py-2.5 cursor-pointer"
            style={{ borderColor: 'var(--border-color)' }}
            onClick={() => setMobileDrawerOpen(true)}
            data-testid="mobile-bar-collapsed"
          >
            {/* Category slot rows */}
            <div className="grid grid-cols-3 gap-2">
              {/* Favourites */}
              <div className="text-center">
                <p className="text-xs font-bold mb-1.5" style={{ color: TIER_COLORS.favourite.color }}>Favourites</p>
                <div className="flex justify-center gap-1.5">
                  {selectedInfo.filter(t => t.seed === 1).map(team => (
                    <div key={team.id} className="w-10 h-7 rounded overflow-hidden shadow-sm" style={{ border: `2px solid ${TIER_COLORS.favourite.color}` }}>
                      <img src={getFlagUrl(team.country_code)} alt={team.name} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {[...Array(Math.max(0, 2 - seedCounts.seed1))].map((_, i) => (
                    <div key={`fav-${i}`} className="w-10 h-7 rounded border-2 border-dashed flex items-center justify-center" style={{ borderColor: TIER_COLORS.favourite.border, background: TIER_COLORS.favourite.bg }}>
                      <span className="text-xs" style={{ color: TIER_COLORS.favourite.color }}>?</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contenders */}
              <div className="text-center">
                <p className="text-xs font-bold mb-1.5" style={{ color: TIER_COLORS.contender.color }}>Contenders</p>
                <div className="flex justify-center gap-1.5">
                  {selectedInfo.filter(t => t.seed === 2).map(team => (
                    <div key={team.id} className="w-10 h-7 rounded overflow-hidden shadow-sm" style={{ border: `2px solid ${TIER_COLORS.contender.color}` }}>
                      <img src={getFlagUrl(team.country_code)} alt={team.name} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {[...Array(Math.max(0, 2 - seedCounts.seed2))].map((_, i) => (
                    <div key={`con-${i}`} className="w-10 h-7 rounded border-2 border-dashed flex items-center justify-center" style={{ borderColor: TIER_COLORS.contender.border, background: TIER_COLORS.contender.bg }}>
                      <span className="text-xs" style={{ color: TIER_COLORS.contender.color }}>?</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Long Shots */}
              <div className="text-center">
                <p className="text-xs font-bold mb-1.5" style={{ color: TIER_COLORS.longshot.color }}>Long Shots</p>
                <div className="flex justify-center gap-1.5">
                  {selectedInfo.filter(t => t.seed === 3 || t.seed === 4).map(team => (
                    <div key={team.id} className="w-10 h-7 rounded overflow-hidden shadow-sm" style={{ border: `2px solid ${TIER_COLORS.longshot.color}` }}>
                      <img src={getFlagUrl(team.country_code)} alt={team.name} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {[...Array(Math.max(0, 2 - (seedCounts.seed3 + seedCounts.seed4)))].map((_, i) => (
                    <div key={`ls-${i}`} className="w-10 h-7 rounded border-2 border-dashed flex items-center justify-center" style={{ borderColor: TIER_COLORS.longshot.border, background: TIER_COLORS.longshot.bg }}>
                      <span className="text-xs" style={{ color: TIER_COLORS.longshot.color }}>?</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Ready / Tap hint */}
            <div className="mt-3 text-center">
              {isValidSelection ? (
                <div
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg font-bold text-base shadow-md active:scale-[0.98] transition-transform"
                  style={{ background: clubPrimary, color: textOnPrimary }}
                  data-testid="mobile-continue-cta"
                >
                  <Trophy size={18} weight="bold" />
                  {demoMode
                    ? `Continue to payment`
                    : (!club.has_stripe ? 'Payments Coming Soon' : `Continue — Pay ${cs(club.currency)}${club.entry_fee?.toFixed(2)}`)}
                </div>
              ) : (
                <span className="text-xs text-gray-400">
                  {selectedTeams.length}/6 selected — tap to view
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
